<template>
  <main class="wallet-add-nft-page">
    <AppNavBar backgroundColor="#fff" :title="t('wallet.addNft')" />

    <section class="tab-row">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="tab-row__item"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        <span>{{ tab.label }}</span>
        <i />
      </button>
    </section>

    <section v-if="activeTab === 'hot'" class="search-wrap">
      <van-field
        v-model="searchText"
        class="search-field"
        :placeholder="t('wallet.enterContractAddressSearch')"
      >
        <template #left-icon>
          <app-image src="@/assets/images/search_icon.png" alt="" class="search-icon" />
        </template>
      </van-field>
    </section>

    <section v-if="activeTab === 'hot'" class="nft-list">
      <button
        v-for="item in filteredHotNfts"
        :key="item.contractAddress || item.name || item.symbol"
        type="button"
        class="nft-row"
      >
        <div class="nft-row__main">
          <app-image v-if="item.img" :src="item.img" alt="" class="nft-row__icon" />
          <div v-else class="nft-row__icon nft-row__icon--fallback">
            {{ nftInitial(item) }}
          </div>

          <div class="nft-row__meta">
            <div data-strong>{{ item.name || item.symbol || 'NFT' }}</div>
            <span>{{ simplifyAddress(item.contractAddress) }}</span>
          </div>
        </div>

        <app-image
          v-if="isAdded(item.contractAddress)"
          src="@/assets/images/remove_blue_icon.png"
          alt=""
          class="nft-row__action"
          @click.stop="removeHotNft(item)"
        />
        <app-image
          v-else
          src="@/assets/images/add_icon_blue.png"
          alt=""
          class="nft-row__action nft-row__action--add"
          @click.stop="addHotNft(item)"
        />
      </button>

      <div v-if="!hotLoading && filteredHotNfts.length === 0" class="empty-state">
        <AppEmptyState plain />
        <span>{{ t('home.noData') }}</span>
      </div>
    </section>

    <section v-else class="custom-wrap">
      <div class="custom-card">
        <div class="field-block field-block--last">
          <!-- <label>{{ t('wallet.nftAddress') }}</label> -->
          <van-field
            v-model="nftAddress"
            class="plain-field plain-field--textarea"
            type="textarea"
            rows="6"
            autosize
            :placeholder="t('wallet.enterNftAddress')"
          />
        </div>
      </div>

      <van-button
        block
        round
        type="primary"
        class="confirm-btn"
        :loading="customSubmitting"
        @click="submitCustomNft"
      >
        {{ t('common.confirm') }}
      </van-button>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { showFailToast, showSuccessToast } from 'vant';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { nftAdd, nftHotList } from '@/api/wallet';
import { useProfileStore } from '@/stores/profile';
import { useWalletNftStore } from '@/stores/wallet-nft';
import AppNavBar from '@/components/AppNavBar.vue';

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const walletNftStore = useWalletNftStore();

const activeTab = ref('hot');
const searchText = ref('');
const hotLoading = ref(false);
const hotNfts = ref([]);
const nftAddress = ref('');
const customSubmitting = ref(false);
const chainNum = computed(() => profileStore.currentNetwork?.chainNum || 56);
const walletAddress = computed(() => profileStore.platformAddress || '');

const tabs = computed(() => [
  { key: 'hot', label: t('wallet.hotNft') },
  { key: 'custom', label: t('wallet.customNft') }
]);

const filteredHotNfts = computed(() => {
  const keyword = String(searchText.value || '').trim().toLowerCase();
  if (!keyword) {
    return hotNfts.value;
  }
  return hotNfts.value.filter((item) => JSON.stringify(item).toLowerCase().includes(keyword));
});

function normalizeNft(item = {}) {
  return {
    ...item,
    contractAddress: item?.contractAddress || item?.address || '',
    protocolType: item?.protocolType || '',
    symbol: item?.symbol || '',
    name: item?.name || item?.symbol || 'NFT',
    img: item?.img || item?.logo || '',
    amount: Number(item?.amount ?? 0),
    chainNum: Number(item?.chainNum ?? chainNum.value)
  };
}

function simplifyAddress(value) {
  const text = String(value || '').trim();
  if (!text) {
    return '--';
  }
  if (text.length <= 12) {
    return text;
  }
  return `${text.slice(0, 6)}...${text.slice(-4)}`;
}

function nftInitial(item) {
  return String(item?.name || item?.symbol || 'N').slice(0, 1).toUpperCase();
}

function isAdded(contractAddress) {
  return walletNftStore.hasNftByContract(contractAddress);
}

function buildLocalNft(item = {}) {
  const normalized = normalizeNft(item);
  return {
    contractAddress: normalized.contractAddress,
    protocolType: normalized.protocolType,
    symbol: normalized.symbol,
    name: normalized.name,
    img: normalized.img,
    amount: normalized.amount,
    chainNum: normalized.chainNum || Number(chainNum.value)
  };
}

function addHotNft(item) {
  const normalized = normalizeNft(item);
  if (!normalized.contractAddress) {
    return;
  }
  walletNftStore.upsertLocalNft(buildLocalNft(normalized), chainNum.value);
  showSuccessToast(t('wallet.addSuccess'));
}

function removeHotNft(item) {
  walletNftStore.removeLocalNft(item.contractAddress, chainNum.value);
}

async function loadHotNfts() {
  hotLoading.value = true;
  try {
    const result = await nftHotList({
      address: walletAddress.value,
      chainNum: chainNum.value
    }, { meta: { showLoading: false } });
    const list = Array.isArray(result?.list) ? result.list : Array.isArray(result) ? result : [];
    hotNfts.value = list.map(normalizeNft);
  } finally {
    hotLoading.value = false;
  }
}

async function submitCustomNft() {
  if (!nftAddress.value) {
    showFailToast(t('wallet.enterNftAddress'));
    return;
  }

  if (!walletAddress.value) {
    showFailToast(t('auth.login'));
    return;
  }

  customSubmitting.value = true;
  try {
    const result = await nftAdd({
      address: walletAddress.value,
      chainNum: chainNum.value,
      contractAddress: nftAddress.value.trim()
    });
    walletNftStore.upsertLocalNft(buildLocalNft(result), chainNum.value);
    showSuccessToast(t('wallet.addSuccess'));
    router.back();
  } finally {
    customSubmitting.value = false;
  }
}

onMounted(async () => {
  await walletNftStore.fetchNftList(walletAddress.value, { meta: { showLoading: false } });
  await loadHotNfts();
});
</script>

<style scoped lang="scss">
.wallet-add-nft-page {
  min-height: var(--app-viewport-height);
  background: #fff;
  padding: 0 16px 24px;
}

.tab-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px 0 0;
}

.tab-row__item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #666;
  font-size: 16px;
  font-weight: 400;
}

.tab-row__item span {
  line-height: 22px;
}

.tab-row__item i {
  width: 50px;
  height: 4px;
  border-radius: 10px;
  opacity: 0;
  background: #3135ac;
  box-shadow: 0 2px 4px rgba(49, 53, 172, 0.4);
}

.tab-row__item.active {
  color: #111;
  font-weight: 700;
}

.tab-row__item.active i {
  opacity: 1;
}

.search-wrap {
  padding-top: 20px;
}

.search-field {
  border-radius: 12px;
  overflow: hidden;
  background: #f4f5f6;
  :deep(.van-field__left-icon) {
    margin-right: 6px;
    margin-top: 3px;
  }
}

.search-icon {
  width: 17px;
  height: 17px;
}

.nft-list {
  padding-top: 12px;
}

.nft-row {
  width: 100%;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border: 0;
  background: transparent;
}

.nft-row__main {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
}

.nft-row__icon {
  width: 40px;
  height: 40px;
  border-radius: 20px;
  object-fit: cover;
  flex-shrink: 0;
}

.nft-row__icon--fallback {
  display: grid;
  place-items: center;
  background: #3135ac;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.nft-row__meta {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 20px;
  text-align: left;
}

.nft-row__meta [data-strong] {
  color: #111;
  font-size: 15px;
  line-height: 21px;
}

.nft-row__meta span {
  color: #999;
  font-size: 12px;
  line-height: 16px;
}

.nft-row__action {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-left: 20px;
}

.nft-row__action--add {
}

.custom-wrap {
  padding-top: 16px;
}

.custom-card {
  border-radius: 16px;
  background: #fff;
}

.field-block {
  padding: 0 0 16px;
}

.field-block label {
  display: block;
  margin-bottom: 10px;
  color: #111;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.field-block--last {
  padding-bottom: 0;
  :deep(.van-field__control) {
    padding-top: 10px;
  }
}

.plain-field {
  border-radius: 14px;
  background: #f7f7f7;
  overflow: hidden;
}

.plain-field--textarea {
  padding-top: 2px;
}

.confirm-btn {
  margin-top: 30px;
  height: 50px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 0 0;
  color: #999;
  font-size: 14px;
}
</style>
